import {
  AddIcon,
  Button,
  ButtonGroup,
  Card,
  DuplicateIcon,
  Grid,
  GridColumn,
  GridRow,
  ImportIcon,
  Modal,
  Row,
  SearchInput,
  Skeleton,
  TabBar,
  Tooltip,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ExclusionTimeframeForm } from '../../../../../../components/ExclusionTimeframe/ExclusionTimeframeForm';
import { useTeachingBlockStore } from '../../../../../../components/TeachingBlockGrid/Contexts/TeachingBlockContext';
import { Exclusions } from '../../../../../../components/TeachingBlockVersion/Exclusions/Exclusions';
import { TeachingBlockVersionTableType } from '../../../../../../components/TeachingBlockVersion/graphql/types';
import { useTeachingBlockVersionData } from '../../../../../../components/TeachingBlockVersion/hooks/useTeachingBlockVersionData';
import { TeachingBlockVersionClassesTable } from '../../../../../../components/TeachingBlockVersion/TeachingBlockVersionDataTables/TeachingBlockVersionClassesTable';
import { TeachingBlockVersionImportClasses } from '../../../../../../components/TeachingBlockVersion/TeachingBlockVersionImportClasses/TeachingBlockVersionImportClasses';
import { useConfirm } from '../../../../../../hooks/useConfirm';
import { useLoadBasicData } from '../../../../../../hooks/useLoadBasicData';
import { cacheTags, useMemorizedCacheTag } from '../../../../../../hooks/useMemorizedCacheTag';
import {
  TeachingBlockResourceType,
  useAddTeachingBlockVersionResourcesMutation,
  useRemoveTeachingBlockVersionResourcesMutation,
} from '../../../../../../types/planung-graphql-client-defs';
import { partition } from '../../../../../../utils/arrayFunc';
import { hexToColorOption } from '../../../../../../utils/colorUtils';
import { createLessonFilterUrl } from '../../../../../../utils/create-lesson-filter-url';
import styles from '../TeachingBlockVersionData.module.scss';

export const PlanTeachingBlockClasses = observer(() => {
  const { t } = useTranslation();
  const { teachingBlockVersionUuid } = useParams();
  const navigate = useNavigate();
  const { confirm, ConfirmationDialog } = useConfirm();

  const [tab, setTab] = useState<'exclusion' | 'condition' | 'division'>('exclusion');

  const [exclusionModalOpen, setExclusionModalOpen] = useState<boolean>(false);
  const [selectedExclusionUuid, setSelectedExclusionUuid] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  const store = useTeachingBlockStore();

  const versionClassesUuids: string[] = Array.from(store.classes.keys());
  const usedPlacedClasses = store.placedCards.flatMap((c) => c.lessonClasses.map((lc) => lc.class.uuid));
  const usedFreeClasses = store.freeCards.flatMap((c) => c.lessonClasses.map((lc) => lc.class.uuid));
  const usedClasses = [...usedPlacedClasses, ...usedFreeClasses];

  const { classesData } = useLoadBasicData({ pause: !teachingBlockVersionUuid });

  const context = useMemorizedCacheTag('TEACHING_BLOCK_AVAILABILITY_EXCLUSION');
  const importClassesContext = useMemo(() => {
    return {
      additionalTypenames: [cacheTags.TEACHINGBLOCK_VERSIONS, cacheTags.CLASSES],
    };
  }, []);

  const [, addVersionResources] = useAddTeachingBlockVersionResourcesMutation();
  const [, removeVersionResources] = useRemoveTeachingBlockVersionResourcesMutation();

  const { versionData } = useTeachingBlockVersionData(teachingBlockVersionUuid);

  const classTableData: TeachingBlockVersionTableType[] = useMemo(() => {
    return (
      classesData?.classes
        .filter((c) => versionClassesUuids.includes(c.uuid))
        .map((c) => {
          const { html, label } = hexToColorOption(c.timetableConfig?.color ?? '');
          return {
            color: {
              color: html,
              label,
            },
            count: 0,
            name: c.name,
            shortName: c.shortName,
            uuid: c.uuid,
          };
        }) ?? []
    );
  }, [classesData?.classes, versionClassesUuids]);

  const [selected, setSelected] = useState<TeachingBlockVersionTableType | null>(classTableData[0] ?? null);

  const selectedExclusionUuids = versionData?.exclusions
    .filter((e) => e.holderUuid === selected?.uuid)
    .map((e) => e.uuid);

  const onExclusionEdit = (uuid: string) => {
    setSelectedExclusionUuid(uuid);
    setExclusionModalOpen(true);
  };

  const onExclusionModalClose = () => {
    setSelectedExclusionUuid(null);
    setExclusionModalOpen(false);
  };

  const importClasses = async (uuids: string[]) => {
    setLoading(true);
    await addVersionResources(
      { versionUuid: teachingBlockVersionUuid ?? '', resourceType: TeachingBlockResourceType.Class, uuids },
      importClassesContext,
    );
    setLoading(false);
    setImportModalOpen(false);
    store.addClasses(classesData?.classes.filter((c) => uuids.includes(c.uuid)) ?? []);
  };

  const handleRemove = async (rows: Row<TeachingBlockVersionTableType>[]) => {
    const [, notUsed] = partition(rows, (row) => usedClasses.includes(row.original.uuid));
    const uuids = notUsed.map((r) => r.original.uuid);

    const res = await confirm({
      title: t('timetableVersion.removeClasses', { count: uuids.length }),
      message: t('timetableVersion.removeClassesDescription', { count: uuids.length }),
    });

    if (res) {
      // Das Backend löst die Klassen aus der Version und löscht ihre Ausschlüsse in dieser Version mit.
      await removeVersionResources(
        { versionUuid: teachingBlockVersionUuid ?? '', resourceType: TeachingBlockResourceType.Class, uuids },
        { additionalTypenames: [...importClassesContext.additionalTypenames, ...context.additionalTypenames] },
      );
      store.removeClasses(uuids);
    }
  };

  return (
    <>
      <Grid>
        <GridRow spacingBottom='none'>
          <SearchInput
            name='class-search'
            placeholder={t('common.search')}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
          <Button
            hierarchy='tertiary'
            icon={<ImportIcon />}
            style={{ marginLeft: 'auto' }}
            disabled={store.readonly}
            onClick={() => setImportModalOpen(true)}
          >
            {t('teachingBlockVersion.import', { import: t('classes.title', { count: 2 }) })}
          </Button>
        </GridRow>
        <GridRow spacingTop='m'>
          <GridColumn width={classTableData.length === 0 ? 12 : 6}>
            <TeachingBlockVersionClassesTable
              versionUuid={teachingBlockVersionUuid}
              search={search}
              versionClassesUuids={versionClassesUuids}
              onRowSelection={(selected) => {
                setSelected(selected);
              }}
              onShowLessonClick={(uuid) => {
                if (teachingBlockVersionUuid) {
                  navigate(
                    createLessonFilterUrl({
                      context: 'epochenplan',
                      filterValue: uuid,
                      versionUuid: teachingBlockVersionUuid,
                      column: 'classes',
                    }),
                  );
                }
              }}
              onRemove={handleRemove}
            />
          </GridColumn>
          {classTableData.length !== 0 && (
            <GridColumn width={6}>
              <Card className={styles['teaching-block-version-data']} contentPadding='none'>
                <small>{t('teachingBlockVersion.specificInfo', { for: selected?.name })}</small>
                <div className={styles['mini-tab']}>
                  <TabBar
                    backgroundColor={'none'}
                    activeTabId={tab}
                    key={selected?.uuid}
                    onValueChange={(content) => setTab(content as 'exclusion' | 'condition' | 'division')}
                    className={styles.tabs}
                    tabs={[
                      {
                        trigger: t('exclusion.title', { count: 2 }),
                        id: 'exclusion',
                        content: (
                          <Exclusions
                            versionUuid={teachingBlockVersionUuid ?? ''}
                            uuids={selectedExclusionUuids ?? []}
                            onEdit={onExclusionEdit}
                            entityType='class'
                          />
                        ),
                      },
                    ]}
                  />
                  <div className={styles['additional-actions']}>
                    {tab === 'exclusion' && (
                      <Tooltip content={t('exclusion.add')}>
                        <Button
                          disabled={store.readonly}
                          hierarchy='ghost'
                          icon={<AddIcon />}
                          onClick={() => setExclusionModalOpen(true)}
                        />
                      </Tooltip>
                    )}
                    {tab === 'condition' && (
                      <ButtonGroup>
                        <Tooltip content={t('constraint.duplicate')}>
                          <Button hierarchy='ghost' icon={<DuplicateIcon />} disabled />
                        </Tooltip>
                        <Tooltip content={t('constraint.edit')}>
                          <Button hierarchy='ghost' icon={<AddIcon />} disabled />
                        </Tooltip>
                      </ButtonGroup>
                    )}
                  </div>
                </div>
              </Card>
            </GridColumn>
          )}
        </GridRow>
        <Modal
          width='s'
          title={t('exclusion.add')}
          isOpen={exclusionModalOpen}
          onRequestClose={() => onExclusionModalClose()}
        >
          <ExclusionTimeframeForm
            type='Class'
            holderUuid={selected?.uuid ?? ''}
            teachingBlockVersionUuid={teachingBlockVersionUuid ?? ''}
            exclusionUuid={selectedExclusionUuid}
            onClose={() => onExclusionModalClose()}
          />
        </Modal>

        {importModalOpen && (
          <Modal
            isOpen={importModalOpen}
            onRequestClose={() => setImportModalOpen(false)}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
            title={t('timetableVersion.import', { import: t('classes.title', { count: 2 }) })}
            height={'l'}
            loading={loading}
          >
            {teachingBlockVersionUuid && importModalOpen && (
              <TeachingBlockVersionImportClasses
                importClasses={importClasses}
                versionUuid={teachingBlockVersionUuid}
                onClose={() => {
                  setImportModalOpen(false);
                }}
              />
            )}
          </Modal>
        )}
      </Grid>
      <ConfirmationDialog />
    </>
  );
});
